import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getAIPicks, getPosterUrl, manhuaCover } from '../services/api';
import { trackEvent } from '../services/analytics';
import AISnippet from './AISnippet';
import RefineChips from './RefineChips';

/**
 * AI Recommendations panel — the interactive, prominent recommender.
 *   - 3 big poster tiles, each with a "💡 why" bound to a real result,
 *   - tone/plot refine chips + a free-text chat ("more like the plot, less gore").
 * Every chip/message re-ranks the picks IN PLACE over the current results.
 *
 * Structural filtering (streamer, year, type) is NOT here — that's the FilterBar,
 * which re-queries the whole result set.
 *
 * Fails soft: empty picks (and no active refinement) → the panel hides.
 *
 * Props: query, results, kind ('movie'|'manhua'), onItemClick
 */

// Static theme class strings (Tailwind JIT needs literals — no interpolation).
const THEMES = {
  light: {
    tone: 'light',
    panel: 'bg-gradient-to-br from-purple-50 to-white border border-purple-100 shadow-sm',
    header: '',
    title: 'text-gray-900',
    sub: 'text-gray-500',
    subStrong: 'text-gray-900',
    dot: 'bg-purple-500',
    tile: 'bg-white hover:shadow-md border border-gray-100 hover:border-purple-200',
    tileTitle: 'text-gray-900 group-hover:text-purple-700',
    tileMeta: 'text-gray-500',
    divider: 'border-gray-100',
    label: 'text-gray-400',
    reset: 'text-gray-400 hover:text-gray-700',
    pill: 'bg-purple-100 text-purple-700 border-purple-200',
    input: 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-purple-300',
    btn: 'bg-purple-600 hover:bg-purple-700',
    empty: 'text-gray-500',
  },
  dark: {
    tone: 'dark',
    panel: 'bg-gradient-to-br from-zinc-900 to-zinc-900/60 border border-rose-500/20 shadow-xl',
    header: 'bg-gradient-to-r from-rose-600/15 to-transparent',
    title: 'text-white',
    sub: 'text-zinc-400',
    subStrong: 'text-zinc-200',
    dot: 'bg-rose-400',
    tile: 'bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20',
    tileTitle: 'text-zinc-100 group-hover:text-rose-300',
    tileMeta: 'text-zinc-500',
    divider: 'border-white/5',
    label: 'text-zinc-500',
    reset: 'text-zinc-400 hover:text-white',
    pill: 'bg-rose-500/20 text-rose-200 border-rose-500/30',
    input: 'bg-zinc-800/80 border-white/10 text-zinc-100 placeholder-zinc-500 focus:border-white/30',
    btn: 'bg-rose-600 hover:bg-rose-500',
    empty: 'text-zinc-400',
  },
  movieDark: {
    tone: 'dark',
    panel: 'bg-gradient-to-br from-slate-800 to-slate-800/60 border border-violet-500/20 shadow-xl',
    header: 'bg-gradient-to-r from-violet-600/15 to-transparent',
    title: 'text-white',
    sub: 'text-slate-400',
    subStrong: 'text-slate-200',
    dot: 'bg-violet-400',
    tile: 'bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20',
    tileTitle: 'text-slate-100 group-hover:text-violet-300',
    tileMeta: 'text-slate-400',
    divider: 'border-white/10',
    label: 'text-slate-500',
    reset: 'text-slate-400 hover:text-white',
    pill: 'bg-violet-500/20 text-violet-200 border-violet-500/30',
    input: 'bg-slate-900/80 border-white/10 text-slate-100 placeholder-slate-500 focus:border-white/30',
    btn: 'bg-violet-600 hover:bg-violet-500',
    empty: 'text-slate-400',
  },
};

const SINGLE_GROUPS = new Set(['vibe', 'plot']);

// Collapse selected chips + chat into the refinement_answers map the API expects.
const buildAnswers = (chips, followUp) => {
  const answers = {};
  for (const c of chips) answers[c.group] = c.value; // vibe / plot are single-value
  if (followUp) answers.follow_up = followUp;
  return Object.keys(answers).length ? answers : null;
};

const AICuratorPanel = ({ query, results, kind = 'movie', onItemClick }) => {
  const [picks, setPicks] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | loading | ready | empty
  const [chips, setChips] = useState([]);        // selected chip objects
  const [followUp, setFollowUp] = useState('');  // active chat instruction
  const [chatInput, setChatInput] = useState('');
  const reqRef = useRef(0);

  const t = THEMES[kind === 'manhua' ? 'dark' : 'movieDark'];
  const answers = useMemo(() => buildAnswers(chips, followUp), [chips, followUp]);
  const answersKey = useMemo(() => JSON.stringify(answers), [answers]);

  // Reset refinements when the underlying search changes.
  useEffect(() => { setChips([]); setFollowUp(''); setChatInput(''); }, [query]);

  // (Re)fetch picks whenever the query, results, or refinements change.
  useEffect(() => {
    if (!query || !results?.length) {
      setStatus('idle');
      setPicks([]);
      return;
    }
    const reqId = ++reqRef.current;
    setStatus('loading');

    (async () => {
      const data = await getAIPicks(query, results, answers, 3);
      if (reqId !== reqRef.current) return; // stale
      const got = Array.isArray(data?.picks) ? data.picks : [];
      if (got.length === 0) { setStatus('empty'); setPicks([]); return; }
      setPicks(got);
      setStatus('ready');
      trackEvent('AI Picks Shown', { query, kind, count: got.length, provider: data?.provider, refined: Boolean(answers) });
    })();
  }, [query, results, kind, answersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (status === 'idle') return null;
  if (status === 'empty' && !answers) return null;

  const byId = {};
  for (const r of results || []) {
    const rid = r?.id ?? r?.tmdb_id ?? r?.manhua_id;
    if (rid != null) byId[String(rid)] = r;
  }

  const tiles =
    status === 'loading'
      ? (results || []).slice(0, 3).map((r) => ({ item: r, why: null }))
      : picks.map((p) => ({ item: byId[String(p.id)], why: p.why })).filter((x) => x.item);

  const posterFor = (item) => (kind === 'manhua' ? manhuaCover(item.poster_path, 512) : getPosterUrl(item.poster_path, 'w342'));
  const titleFor = (item) => item.title || item.name || '';
  const yearFor = (item) => {
    const d = item.release_date || item.first_air_date || '';
    return item.year || (d ? String(d).slice(0, 4) : '');
  };
  const ratingFor = (item) => item.vote_average || item.rating || 0;

  const handleClick = (item) => {
    trackEvent('AI Pick Clicked', { kind, id: item.id, title: titleFor(item) });
    onItemClick && onItemClick(item);
  };

  const toggleChip = (chip) => {
    const id = `${chip.group}:${chip.value}`;
    setChips((prev) => {
      const exists = prev.some((c) => `${c.group}:${c.value}` === id);
      if (exists) return prev.filter((c) => `${c.group}:${c.value}` !== id);
      const cleared = SINGLE_GROUPS.has(chip.group) ? prev.filter((c) => c.group !== chip.group) : prev;
      return [...cleared, chip];
    });
    trackEvent('Curator Chip Toggled', { kind, group: chip.group, value: chip.value });
  };

  const selectedKeys = chips.map((c) => `${c.group}:${c.value}`);

  const submitChat = (e) => {
    e.preventDefault();
    const text = chatInput.trim();
    if (!text) return;
    setFollowUp(text);
    setChatInput('');
    trackEvent('Curator Chat Sent', { kind, length: text.length });
  };

  const resetRefine = () => { setChips([]); setFollowUp(''); };
  const hasRefine = chips.length > 0 || Boolean(followUp);

  return (
    <div className={`mb-8 rounded-2xl overflow-hidden ${t.panel}`}>
      <div className={`px-5 sm:px-6 pt-5 pb-4 ${t.header}`}>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-2xl">✨</span>
          <h3 className={`text-lg sm:text-xl font-bold ${t.title}`}>AI Recommendations for you</h3>
          {status === 'loading' && (
            <span className="ml-2 flex space-x-1">
              {[0, 150, 300].map((d) => (
                <span key={d} className={`w-1.5 h-1.5 ${t.dot} rounded-full animate-bounce`} style={{ animationDelay: `${d}ms` }} />
              ))}
            </span>
          )}
        </div>
        <p className={`text-sm ${t.sub}`}>
          Hand-picked from your results for <span className={`font-medium ${t.subStrong}`}>"{query}"</span> — like a friend who's seen everything. Tweak it below.
        </p>
      </div>

      {/* Picks */}
      <div className="px-5 sm:px-6 py-5">
        {tiles.length === 0 ? (
          <p className={`text-sm py-4 ${t.empty}`}>No picks match those tweaks. Loosen a chip or rephrase your ask.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {tiles.map(({ item, why }, i) => {
              const poster = posterFor(item);
              return (
                <button
                  key={item.id ?? i}
                  onClick={() => handleClick(item)}
                  className={`group text-left rounded-xl transition-all p-3 flex gap-3 ${t.tile}`}
                >
                  <div className="relative w-24 sm:w-28 flex-shrink-0 aspect-[2/3] rounded-lg overflow-hidden bg-gray-200">
                    {poster ? (
                      <img
                        src={poster}
                        alt={titleFor(item)}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-[10px] px-1 text-center">
                        {titleFor(item)}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className={`font-semibold text-sm sm:text-base line-clamp-2 transition-colors ${t.tileTitle}`}>
                      {titleFor(item)}
                    </h4>
                    <p className={`text-xs mt-0.5 ${t.tileMeta}`}>
                      {ratingFor(item) > 0 && <span className="text-amber-500">★ {Number(ratingFor(item)).toFixed(1)}</span>}
                      {ratingFor(item) > 0 && yearFor(item) && <span> · </span>}
                      {yearFor(item)}
                    </p>
                    <AISnippet why={why} loading={status === 'loading'} tone={t.tone} />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Refine controls */}
      <div className={`px-5 sm:px-6 pb-5 pt-1 border-t space-y-3 ${t.divider}`}>
        <div className="flex items-center justify-between">
          <span className={`text-xs uppercase tracking-wide font-semibold ${t.label}`}>Fine-tune</span>
          {hasRefine && (
            <button onClick={resetRefine} className={`text-xs underline ${t.reset}`}>reset</button>
          )}
        </div>

        <RefineChips selected={selectedKeys} onToggle={toggleChip} tone={t.tone} />

        {followUp && (
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-2 text-xs border rounded-full px-3 py-1 ${t.pill}`}>
              💬 {followUp}
              <button onClick={() => setFollowUp('')} aria-label="Clear instruction" className="hover:opacity-70">×</button>
            </span>
          </div>
        )}

        <form onSubmit={submitChat} className="flex gap-2">
          <input
            type="text"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            placeholder="Ask the AI… e.g. 'more like the plot, less gore'"
            className={`flex-1 px-4 py-2.5 rounded-xl border text-sm focus:outline-none transition-all ${t.input}`}
          />
          <button
            type="submit"
            disabled={!chatInput.trim()}
            className={`px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${t.btn}`}
          >
            Ask
          </button>
        </form>
      </div>
    </div>
  );
};

export default AICuratorPanel;
